import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import Breadcrumb from "../components/Breadcrumb";
import LeagueContextActions from "../components/LeagueContextActions";
import { safeFetch, fetchWithRetry } from "../utils/api";
import { toast } from "sonner";
import { Settings, ChevronDown, ChevronUp } from "lucide-react";
import usePageMeta from "../utils/usePageMeta";

const LeagueSettingsPage = () => {
    const { provider, leagueYear, leagueId } = useParams();
    const navigate = useNavigate();

    const [leagueData, setLeagueData] = useState(null);
    const [fetchError, setFetchError] = useState(null);

    // Discord state
    const [discordWebhookUrl, setDiscordWebhookUrl] = useState("");
    const [discordSavedUrl, setDiscordSavedUrl] = useState(null);
    const [discordEditing, setDiscordEditing] = useState(true); // false = locked/saved view
    const [discordGuideOpen, setDiscordGuideOpen] = useState(false);
    const [discordSaving, setDiscordSaving] = useState(false);
    const [discordTesting, setDiscordTesting] = useState(false);

    // League context (persistent AI notes)
    const [leagueContext, setLeagueContext] = useState("");
    const [leagueContextSaving, setLeagueContextSaving] = useState(false);

    usePageMeta({
        title: leagueData
            ? `Settings — ${leagueData.league_name} (${leagueYear})`
            : `League Settings — ${leagueYear}`,
    });

    const leagueUrl = `/fantasy_stats/league/${provider}/${leagueYear}/${leagueId}/`;
    const leagueContextPlaceholder = provider === "demo"
        ? "Example:\nLyra North and Owen Slate have traded the top seed all season.\nTessa Bloom is known for careful lineup decisions, while Priya Vale usually makes the playoff bubble interesting."
        : "Example:\nDesi is the commissioner. Carmine (dad) is the defending champion.\nNikki and Isabella have a rivalry. Ben is Nikki's boyfriend.";

    useEffect(() => {
        safeFetch(`/api/league/${provider}/${leagueYear}/${leagueId}/`, {}, false, 2)
            .then((data) => {
                if (data?.redirect) {
                    navigate(data.redirect);
                } else if (!data?.is_owner) {
                    // Non-owners shouldn't access settings
                    navigate(leagueUrl);
                } else {
                    setLeagueData(data);
                    if (data.discord_webhook_url) {
                        setDiscordSavedUrl(data.discord_webhook_url);
                        setDiscordWebhookUrl(data.discord_webhook_url);
                        setDiscordEditing(false);
                    }
                    // Fetch league_context separately (requires auth)
                    safeFetch(`/api/league/${data.id}/league-context/`, {}, true, 1)
                        .then((ctx) => { if (ctx?.league_context != null) setLeagueContext(ctx.league_context); })
                        .catch(() => {});
                }
            })
            .catch((err) => {
                console.error("ERROR: /api/league/:provider/:year/:id", err);
                setFetchError(err);
            });
    }, [leagueYear, leagueId, navigate, leagueUrl]); // eslint-disable-line react-hooks/exhaustive-deps

    if (fetchError) throw fetchError;

    const handleDiscordSave = async () => {
        if (!leagueData?.id) return;
        setDiscordSaving(true);
        try {
            const resp = await fetchWithRetry(
                `/api/league/${leagueData.id}/discord-webhook/`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ discord_webhook_url: discordWebhookUrl }),
                },
                0
            );
            const data = await resp.json();
            if (resp.ok) {
                setDiscordSavedUrl(data.discord_webhook_url);
                setDiscordEditing(false);
                toast.success("Discord webhook saved!");
            } else {
                toast.error(data.error || "Failed to save webhook URL.");
            }
        } catch {
            toast.error("An unexpected error occurred.");
        } finally {
            setDiscordSaving(false);
        }
    };

    const handleDiscordTest = async () => {
        if (!leagueData?.id) return;
        setDiscordTesting(true);
        try {
            const resp = await fetchWithRetry(
                `/api/league/${leagueData.id}/discord-webhook/test/`,
                { method: "POST" },
                0
            );
            const data = await resp.json();
            if (resp.ok && data.success) {
                toast.success("Test message sent to Discord!");
            } else {
                toast.error(data.error || "Failed — check your webhook URL.");
            }
        } catch {
            toast.error("An unexpected error occurred.");
        } finally {
            setDiscordTesting(false);
        }
    };

    const handleDiscordRemove = async () => {
        if (!leagueData?.id) return;
        try {
            const resp = await fetchWithRetry(
                `/api/league/${leagueData.id}/discord-webhook/`,
                { method: "DELETE" },
                0
            );
            if (resp.ok) {
                setDiscordSavedUrl(null);
                setDiscordWebhookUrl("");
                setDiscordEditing(true);
                toast.success("Discord webhook removed.");
            } else {
                toast.error("Failed to remove webhook.");
            }
        } catch {
            toast.error("An unexpected error occurred.");
        }
    };

    const handleLeagueContextSave = async () => {
        if (!leagueData?.id) return;
        setLeagueContextSaving(true);
        try {
            const resp = await fetchWithRetry(
                `/api/league/${leagueData.id}/league-context/`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ league_context: leagueContext }),
                },
                0
            );
            const data = await resp.json();
            if (resp.ok) {
                toast.success("League context saved!");
            } else {
                toast.error(data.error || "Failed to save league context.");
            }
        } catch {
            toast.error("An unexpected error occurred.");
        } finally {
            setLeagueContextSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-bg">
            <Header />

            {/* ── Hero Band ── */}
            <div className="bg-forest border-b-4 border-mustard">
                <div className="max-w-5xl mx-auto px-6 py-6">
                    <Breadcrumb
                        items={[
                            { label: "Dashboard", to: "/dashboard" },
                            {
                                label: leagueData?.league_name ?? "…",
                                to: leagueData ? leagueUrl : undefined,
                            },
                            { label: "Settings" },
                        ]}
                    />
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                        <div>
                            <p className="text-mustard text-xs font-sans font-bold uppercase tracking-widest mb-2">
                                {leagueYear} Season
                            </p>
                            <h1 className="font-display text-4xl font-black text-white leading-none flex items-center gap-3">
                                <Settings className="w-8 h-8 text-mustard" />
                                {leagueData
                                    ? leagueData.league_name
                                    : <span className="inline-block w-64 h-9 bg-white/20 rounded animate-pulse" />
                                }
                            </h1>
                            <p className="text-white/50 font-sans text-sm font-semibold mt-2">
                                League Settings
                            </p>
                        </div>
                        <LeagueContextActions league={leagueData} provider={provider} leagueYear={leagueYear} leagueId={leagueId} activePage="settings" />
                    </div>
                </div>
            </div>

            {/* ── Settings Content ── */}
            <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">

                {/* ── Discord Integration ── */}
                <section>
                    <h2 className="font-display text-xl font-bold text-ds-text mb-1">Notifications</h2>
                    <p className="text-text-muted text-sm mb-6">
                        Connect your league to external services to receive automated updates.
                    </p>

                    <div className="border border-border-card rounded-xl bg-surface p-6 max-w-xl">
                        <div className="flex items-center gap-2 mb-3">
                            {/* Discord blurple icon */}
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#5865F2" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                            </svg>
                            <span className="font-sans font-bold text-ds-text">Discord Integration</span>
                        </div>
                        <p className="text-sm text-text-muted mb-4 leading-relaxed">
                            Post a weekly recap to your Discord server automatically every <strong className="text-ds-text">Tuesday at 9:00 AM ET</strong> after scoring closes.
                            No bot setup required — just paste a webhook URL below.
                        </p>

                        {/* Input row */}
                        <div className="flex flex-col gap-2 mb-3 sm:flex-row">
                            <input
                                type="url"
                                value={discordWebhookUrl}
                                onChange={(e) => setDiscordWebhookUrl(e.target.value)}
                                placeholder="https://discord.com/api/webhooks/..."
                                disabled={!discordEditing}
                                className={`w-full min-w-0 text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5865F2] transition-colors sm:flex-1 ${
                                    discordEditing
                                        ? "border-border bg-bg text-ds-text placeholder:text-text-muted"
                                        : "border-border bg-surface text-text-muted cursor-not-allowed"
                                }`}
                            />
                            {discordEditing ? (
                                <button
                                    onClick={handleDiscordSave}
                                    disabled={discordSaving || !discordWebhookUrl.trim()}
                                    className="w-full rounded-lg bg-[#5865F2] px-4 py-2 text-sm font-bold font-sans text-white transition-colors hover:bg-[#4752c4] disabled:opacity-50 sm:w-auto"
                                >
                                    {discordSaving ? "Saving…" : "Save"}
                                </button>
                            ) : (
                                <button
                                    onClick={() => setDiscordEditing(true)}
                                    className="w-full rounded-lg border border-border px-4 py-2 text-sm font-bold font-sans text-text-muted transition-colors hover:border-ds-text hover:text-ds-text sm:w-auto"
                                >
                                    Edit
                                </button>
                            )}
                        </div>

                        {/* Action row */}
                        <div className="flex flex-col gap-2 mb-4 sm:flex-row">
                            <button
                                onClick={handleDiscordTest}
                                disabled={discordTesting || !discordSavedUrl}
                                className="w-full rounded-lg border border-[#5865F2] px-4 py-2 text-sm font-bold font-sans text-[#5865F2] transition-colors hover:bg-[#5865F2]/10 disabled:opacity-40 sm:w-auto"
                            >
                                {discordTesting ? "Sending…" : "Send Test Message"}
                            </button>
                            {discordSavedUrl && (
                                <button
                                    onClick={handleDiscordRemove}
                                    className="w-full rounded-lg border border-red-300 px-4 py-2 text-sm font-bold font-sans text-red-500 transition-colors hover:bg-red-50 sm:w-auto"
                                >
                                    Remove
                                </button>
                            )}
                        </div>

                        {/* Collapsible setup guide */}
                        <button
                            onClick={() => setDiscordGuideOpen((o) => !o)}
                            className="flex items-center gap-1 text-xs text-text-muted hover:text-ds-text transition-colors"
                        >
                            {discordGuideOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            How to create a Discord webhook
                        </button>
                        {discordGuideOpen && (
                            <div className="mt-3 text-sm text-text-muted space-y-2 border-t border-border pt-3">
                                <p><strong className="text-ds-text">1. Open your Discord server settings.</strong><br />
                                Go to your server → click the server name → <em>Server Settings</em> → <em>Integrations</em> → <em>Webhooks</em>.</p>
                                <p><strong className="text-ds-text">2. Create a new webhook.</strong><br />
                                Click <em>"New Webhook"</em>, give it a name (e.g. <em>Fantasy Recap</em>), and choose the channel where updates should appear. Click <em>"Copy Webhook URL"</em>.</p>
                                <p><strong className="text-ds-text">3. Paste the URL above and click Save.</strong><br />
                                Then click <em>"Send Test Message"</em> to confirm it's working.</p>
                                <p className="text-xs italic">Keep your webhook URL private — anyone with it can post to your channel.</p>
                            </div>
                        )}
                    </div>
                </section>

                {/* ── AI League Context ── */}
                <section className="mt-10">
                    <h2 className="font-display text-xl font-bold text-ds-text mb-1">AI Weekly Report</h2>
                    <p className="text-text-muted text-sm mb-6">
                        Provide persistent context about your league that gets included every time the AI generates a weekly narrative.
                        Include family relationships, rivalries, team histories, division lore — anything that doesn't change week-to-week.
                    </p>

                    <div className="border border-border-card rounded-xl bg-surface p-6 max-w-xl">
                        <div className="flex items-center gap-2 mb-3">
                            <svg className="w-4 h-4 text-mustard" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                            </svg>
                            <span className="font-sans font-bold text-ds-text">League Context</span>
                        </div>
                        <p className="text-sm text-text-muted mb-3 leading-relaxed">
                            This text is stored in your league settings and automatically included every week.
                            Use plain sentences — the AI reads it directly.
                        </p>
                        <textarea
                            value={leagueContext}
                            onChange={(e) => setLeagueContext(e.target.value)}
                            placeholder={leagueContextPlaceholder}
                            rows={6}
                            className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-bg text-ds-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-mustard resize-y mb-3"
                        />
                        <button
                            onClick={handleLeagueContextSave}
                            disabled={leagueContextSaving}
                            className="w-full rounded-lg bg-mustard px-4 py-2 text-sm font-bold font-sans text-forest transition-colors hover:bg-yellow-400 disabled:opacity-50 sm:w-auto"
                        >
                            {leagueContextSaving ? "Saving…" : "Save"}
                        </button>
                    </div>
                </section>

                {/* ── League Cup ── */}
                <section className="mt-10">
                    <h2 className="font-display text-xl font-bold text-ds-text mb-1">League Cup</h2>
                    <p className="text-text-muted text-sm mb-6">
                        Run an NBA Cup–style in-season tournament on top of your league's real weekly scores.
                    </p>
                    <div className="border border-border-card rounded-xl bg-surface p-6 max-w-xl">
                        <p className="text-sm text-text-muted mb-3">
                            League Cup settings now live on the League Cup page.
                        </p>
                        <Link
                            to={`/fantasy_stats/league-cup/${provider}/${leagueYear}/${leagueId}/?tab=settings`}
                            className="inline-flex w-full justify-center rounded-lg bg-mustard px-4 py-2 text-sm font-bold font-sans text-forest transition-colors hover:bg-yellow-400 sm:w-auto"
                        >
                            Open League Cup settings
                        </Link>
                    </div>
                </section>

            </main>
        </div>
    );
};

export default LeagueSettingsPage;
